import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { webhookApi } from "../../api/client";
import { Button, Badge, Modal } from "../../components/ui";
import ChannelIcon from "../../components/ChannelIcon";
import Icon from "../../components/Icon";
import WebhookFormModal from "./WebhookFormModal";
import WebhookEventLog from "./WebhookEventLog";

const STATUS_BADGE = {
  active: { bg: `${COLORS.green}12`, color: COLORS.green, border: `${COLORS.green}25` },
  paused: { bg: `${COLORS.orange}12`, color: COLORS.orange, border: `${COLORS.orange}25` },
  archived: { bg: `${COLORS.textDim}12`, color: COLORS.textDim, border: `${COLORS.textDim}25` },
};

// Exported for unit testing — badge style for a webhook status, with an
// archived-style fallback for unknown states.
export const resolveStatusBadge = (status) => STATUS_BADGE[status] || STATUS_BADGE.archived;

// Exported for unit testing — the pause/activate toggle target: an active
// webhook pauses, anything else activates.
export const nextWebhookStatus = (status) => (status === "active" ? "paused" : "active");

export default function WebhooksView({ currentUser }) {
  const bp = useBreakpoint();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [copied, setCopied] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await webhookApi.list();
      if (res.success) setWebhooks(res.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (data) => {
    try {
      const res = await webhookApi.create(data);
      if (res.success) {
        setNewSecret(res.data.secret);
        setShowForm(false);
        load();
      }
    } catch {}
  };

  const handleUpdate = async (data) => {
    try {
      const res = await webhookApi.update(editingWebhook.id, data);
      if (res.success) {
        setEditingWebhook(null);
        load();
      }
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this webhook? All event history will be lost.")) return;
    setDeleting(id);
    try {
      await webhookApi.delete(id);
      load();
    } catch {}
    setDeleting(null);
  };

  const handleToggleStatus = async (wh) => {
    const next = nextWebhookStatus(wh.status);
    try {
      await webhookApi.updateStatus(wh.id, next);
      load();
    } catch {}
  };

  const handleRegenerateSecret = async (wh) => {
    if (!confirm(`Regenerate secret for "${wh.name}"? The old secret will stop working immediately.`)) return;
    setRegenerating(wh.id);
    try {
      const res = await webhookApi.regenerateSecret(wh.id);
      if (res.success) setNewSecret(res.data.secret);
    } catch (e) {
      alert("Failed to regenerate secret: " + e.message);
    }
    setRegenerating(null);
  };

  const handleTest = async (wh) => {
    try {
      const res = await webhookApi.test(wh.id);
      if (res.success) {
        alert(`Test result:\nRecipient: ${res.data.recipient || "none"}\nMessage: ${res.data.message || "none"}\nChannel: ${res.data.channel}`);
      }
    } catch (e) {
      alert("Test failed: " + e.message);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // Show event log
  if (selectedWebhook) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <WebhookEventLog webhook={selectedWebhook} onClose={() => setSelectedWebhook(null)} />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: bp.isMobile ? "16px 12px" : "20px 28px",
        borderBottom: `1px solid ${COLORS.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>Webhook Integrations</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
            Connect external systems to trigger messages, calls, and agent actions
          </div>
        </div>
        <Button onClick={() => setShowForm(true)}>+ Create Webhook</Button>
      </div>

      {/* Secret reveal modal */}
      <Modal open={!!newSecret} onClose={() => setNewSecret(null)} title="Webhook Secret" width={460}>
        <div style={{
          padding: "10px 12px", borderRadius: 8, background: `${COLORS.orange}10`,
          border: `1px solid ${COLORS.orange}25`, marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, color: COLORS.orange, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="status.warning" size={14} color={COLORS.orange} />
            Save this secret now — it won't be shown again.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input readOnly value={newSecret || ""} style={{
            flex: 1, padding: "8px 10px", borderRadius: 7, fontSize: 11,
            fontFamily: "monospace", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            color: COLORS.text, outline: "none",
          }} />
          <Button variant={copied === "secret" ? "primary" : "secondary"} size="sm"
            onClick={() => copyToClipboard(newSecret, "secret")}
            style={copied === "secret" ? { background: COLORS.green, color: "#fff", border: "none" } : {}}>
            {copied === "secret" ? "Copied!" : "Copy"}
          </Button>
        </div>
      </Modal>

      {/* Webhook list */}
      <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 12 : "16px 28px" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>Loading webhooks...</div>
        ) : webhooks.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{"\u21C4"}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>No webhooks yet</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Create a webhook to receive events from external systems</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {webhooks.map((wh, idx) => {
              const badge = resolveStatusBadge(wh.status);
              return (
                // i2-visual-identity-v2 §10.2 B3 — Webhook row card.
                <motion.div
                  key={wh.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring", stiffness: 400, damping: 25,
                    delay: Math.min(idx * 0.05, 0.4),
                  }}
                  whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
                  style={{
                    padding: "14px 18px",
                    borderRadius: RADIUS.inner,
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: MATERIALS.spatialShadow(),
                  }}>
                  {/* Top row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <ChannelIcon channel={wh.channel} size={16} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{wh.name}</span>
                        <Badge color={badge.color}>{wh.status}</Badge>
                      </div>
                      {wh.description && (
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {wh.description}
                        </div>
                      )}
                    </div>
                    {/* Stats */}
                    <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accentText }}>{wh.event_count || 0}</div>
                        <div style={{ fontSize: 9, color: COLORS.textDim }}>Events</div>
                      </div>
                      <div style={{ textAlign: "center", minWidth: 70 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>
                          {wh.last_triggered_at ? new Date(wh.last_triggered_at).toLocaleDateString() : "Never"}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textDim }}>Last triggered</div>
                      </div>
                    </div>
                  </div>

                  {/* Info row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <Badge color={COLORS.textMuted} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>{wh.channel}</Badge>
                    <Badge color={COLORS.textMuted} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>{(wh.action_type || "").replace(/_/g, " ")}</Badge>
                    <Badge color={COLORS.textMuted} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>{wh.auth_mode}</Badge>
                  </div>

                  {/* Inbound URL */}
                  {wh.inbound_url && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                      borderRadius: 7, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      marginBottom: 10,
                    }}>
                      <span style={{ fontSize: 10, color: COLORS.textDim, flexShrink: 0 }}>URL:</span>
                      <span style={{
                        fontSize: 10, color: COLORS.cyan, fontFamily: "monospace",
                        flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{wh.inbound_url}</span>
                      <Button variant="secondary" size="sm"
                        onClick={() => copyToClipboard(wh.inbound_url, wh.id)}
                        style={copied === wh.id ? { background: COLORS.green, color: "#fff", border: "none" } : { padding: "3px 8px", fontSize: 9 }}>
                        {copied === wh.id ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 4 }}>
                    {[
                      { label: "Events", action: () => setSelectedWebhook(wh) },
                      { label: "Edit", action: () => setEditingWebhook(wh) },
                      { label: wh.status === "active" ? "Pause" : "Activate", action: () => handleToggleStatus(wh) },
                      { label: regenerating === wh.id ? "Regenerating..." : "Regenerate Secret", action: () => handleRegenerateSecret(wh), disabled: regenerating === wh.id },
                      { label: "Test", action: () => handleTest(wh) },
                      { label: deleting === wh.id ? "Deleting..." : "Delete", action: () => handleDelete(wh.id), danger: true },
                    ].map(btn => (
                      <Button key={btn.label} variant={btn.danger ? "danger" : "secondary"} size="sm"
                        onClick={btn.action} disabled={btn.disabled || (deleting === wh.id && btn.danger)}>
                        {btn.label}
                      </Button>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <WebhookFormModal onSave={handleCreate} onClose={() => setShowForm(false)} />
      )}
      {editingWebhook && (
        <WebhookFormModal webhook={editingWebhook} onSave={handleUpdate} onClose={() => setEditingWebhook(null)} />
      )}
    </div>
  );
}
